import { useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SupplierListPage } from "@shared-core/data/purchase-ledger-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  extractErrorDetailMessage,
  extractFieldErrors,
} from "@shared-core/data/validation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";

type FieldErrors = Partial<Record<"name" | "payment_terms_days", string>>;

function isNameInUse(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const detail = (err.body as { detail?: { reason_code?: unknown } } | null)
    ?.detail;
  return detail?.reason_code === "supplier_name_in_use";
}

/**
 * Create a supplier in One — an overhead supplier has to exist before its
 * first purchase invoice can be captured. Posts to the create URL the suppliers list
 * carried; the purchase ledger's `create_supplier` decides what is allowed (a
 * name not already used by an open supplier, payment terms of 1–365 days or
 * blank) and its refusals sit under the field they belong to, per the API
 * validation rule. One gives the supplier its `SUP-` number.
 */
export function NewSupplierDialog({
  list,
  onCreated,
}: {
  list: Pick<SupplierListPage, "createUrl">;
  onCreated: (created: { supplierNo: string; name: string }) => void;
}) {
  const adapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [terms, setTerms] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function reset() {
    setName("");
    setTerms("");
    setErrors({});
    setFailure(null);
  }

  async function submit() {
    setErrors({});
    setFailure(null);
    const trimmedTerms = terms.trim();
    const days = trimmedTerms === "" ? null : Number(trimmedTerms);
    if (days !== null && !Number.isInteger(days)) {
      setErrors({
        payment_terms_days: "Payment terms are a whole number of days.",
      });
      return;
    }
    setSaving(true);
    try {
      const created = await adapter.createSupplier(list, {
        name: name.trim(),
        paymentTermsDays: days,
      });
      toast.success(`${created.name} created as ${created.supplierNo}`);
      setOpen(false);
      reset();
      onCreated(created);
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (isNameInUse(err)) {
        // The ledger refuses a name an open supplier already has, naming that supplier.
        setErrors({
          name: extractErrorDetailMessage(err) ?? "That name is in use.",
        });
      } else if (fieldErrors.length > 0) {
        setErrors(
          Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
        );
      } else {
        setFailure(
          err instanceof Error ? err.message : "The supplier was not created.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const errorCount = Object.keys(errors).length;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">New supplier</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New supplier</DialogTitle>
          <DialogDescription>
            For a supplier One buys from directly, such as an overhead like
            hosting or insurance, or a new fulfilment supplier. The supplier
            will get a supplier number (SUP-…) and won't be added to Tharstern.
          </DialogDescription>
        </DialogHeader>
        {errorCount > 0 && (
          <p role="alert" className="text-sm text-red-600">
            {errorCount === 1
              ? "1 thing to fix"
              : `${errorCount} things to fix`}{" "}
            before the supplier can be created.
          </p>
        )}
        {failure && (
          <p role="alert" className="text-sm text-red-600">
            {failure}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor="new-supplier-name" className="text-sm font-medium">
            Name
          </label>
          <Input
            id="new-supplier-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Hiscox"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={
              errors.name ? "new-supplier-name-error" : undefined
            }
          />
          {errors.name && (
            <p id="new-supplier-name-error" className="text-sm text-red-600">
              {errors.name}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label htmlFor="new-supplier-terms" className="text-sm font-medium">
            Payment terms, days
          </label>
          <Input
            id="new-supplier-terms"
            inputMode="numeric"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            placeholder="Blank when unknown — due dates come from each purchase invoice"
            aria-invalid={errors.payment_terms_days ? true : undefined}
            aria-describedby={
              errors.payment_terms_days ? "new-supplier-terms-error" : undefined
            }
          />
          {errors.payment_terms_days && (
            <p id="new-supplier-terms-error" className="text-sm text-red-600">
              {errors.payment_terms_days}
            </p>
          )}
        </div>
        <Button onClick={submit} disabled={saving}>
          Create supplier
        </Button>
      </DialogContent>
    </Dialog>
  );
}
